import { useEffect, useState } from "react"
import { HStack, Separator, Spinner, Stack } from "@chakra-ui/react"
import { Button, Section, Text } from "@/components"
import { useDropboxDocuments } from "@/hooks"
import { ComplianceType, Deal } from "@/types"
import { BackgroundCheckFormComponent, CompletedTaskComponent, ComplianceTab, NdaFormComponent, PendingDocumentComponent } from "./components"
import { ALL_STEPS, BC_FORM_STEP_INDEX, COMPLETE_NDA_FORM_STEP_INDEX, SIGN_BC_STEP_INDEX, SIGN_NDA_STEP_INDEX } from "@/constants"
import { isCompleted } from "./utils"

interface Props {
  isLoading: boolean
  deals: Deal[]
}

export const ComplianceComponent = ({ deals, isLoading }: Props) => {
  const COMPLIANCE_TYPES = [ComplianceType.NDA, ComplianceType.BACKGROUND_CHECK]

  const [currentDeal, setCurrentDeal] = useState<Deal | null>(null)
  const [complianceTypes, setComplianceTypes] = useState<ComplianceType[]>(COMPLIANCE_TYPES)
  const [currentComplianceType, setCurrentComplianceType] = useState<ComplianceType | null>(COMPLIANCE_TYPES[1])
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(COMPLETE_NDA_FORM_STEP_INDEX)

  const { data: dropboxDocuments, isLoading: isLoadingDocs } = useDropboxDocuments(currentDeal?.id ?? undefined); 

  useEffect(() => {
    if (deals && deals.length > 0) {
      setCurrentDeal(deals[0])
    }
  }, [deals])

  useEffect(() => {
    if (currentDeal) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.portalStage);
      if (index < BC_FORM_STEP_INDEX) {
        setComplianceTypes([ComplianceType.NDA])
        setCurrentComplianceType(COMPLIANCE_TYPES[0])
        setCurrentStepIndex(index)
        return
      } else {
        setComplianceTypes(COMPLIANCE_TYPES)
        setCurrentStepIndex(index)
      }
    }
  }, [currentDeal])

  const complianceFormComponent = () => {
    if (!currentComplianceType) return null
    if (!currentDeal) return null

    if (isCompleted(currentComplianceType, dropboxDocuments || [])) {
      return <CompletedTaskComponent complianceType={currentComplianceType} />
    }

    switch (currentComplianceType) {
      case ComplianceType.BACKGROUND_CHECK:
        if (currentStepIndex > SIGN_BC_STEP_INDEX) {
          return <CompletedTaskComponent complianceType={ComplianceType.BACKGROUND_CHECK} />
        } else if (currentStepIndex === SIGN_BC_STEP_INDEX) {
          return <PendingDocumentComponent complianceType={ComplianceType.BACKGROUND_CHECK} />
        }

        return <BackgroundCheckFormComponent currentDeal={currentDeal} />
      case ComplianceType.NDA:
        if (currentStepIndex > SIGN_NDA_STEP_INDEX) {
          return <CompletedTaskComponent complianceType={ComplianceType.NDA} />
        } else if (currentStepIndex === SIGN_NDA_STEP_INDEX) {
          return <PendingDocumentComponent complianceType={ComplianceType.NDA} />
        } else {
          return <NdaFormComponent isInitialScreen={false} />
        }
    }
  }

  return (
    <Section>
      <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
        Compliance
      </Text>
      {
        isLoading ? (
          <HStack w="100%" justifyContent="center" alignItems="center" p={10}>
            <Spinner />
          </HStack>
        ) : (
          <>
            <HStack mt={5} gap={2}>
              {
                deals && deals.map((deal, index) => (
                  <Button
                    key={index}
                    variant={ deal.id === currentDeal?.id ? "solid" : "outline"}
                    onClick={() => setCurrentDeal(deal)}
                  >
                    {deal.company.name}
                  </Button>
                ))
              }
            </HStack>
            <HStack mt={10}>
              {
                complianceTypes.map((type, index) => (
                  <ComplianceTab
                    key={index}
                    isLoading={isLoadingDocs}
                    type={type}
                    currentComplianceType={currentComplianceType}
                    complianceDocuments={dropboxDocuments}
                    setCurrentComplianceType={setCurrentComplianceType}
                  />
                ))
              }
            </HStack>
            <Separator mt={0} />
            <Stack>
              {
                complianceFormComponent()
              }
            </Stack>
          </>
        )
      }
    </Section>
  )
}
